import React, { useCallback, useEffect, useMemo, useState } from "react";
import { format, parseISO, addDays } from "date-fns";
import { mdyDowTime } from "@/lib/dateFmt";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AlertTriangle, Clock, Trophy, Users2, X, Zap, CheckCircle2, Info, Crown, BadgeCheck, Ban } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import NsoManagerView from "@/components/NsoManagerView";

// Small utility — build a Sun-anchored 6-row calendar grid for a month
function monthGrid(monthIso) {
  const [y, m] = monthIso.slice(3).split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const startDow = (first.getDay());        // 0=Sun
  const gridStart = addDays(first, -startDow);
  return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
}

export default function NsoPage({ facilityId }) {
  const { user } = useAuth();
  // Managers/admins see the manager overview — one facility at a time.
  if (user?.role !== "employee") {
    return <NsoManagerView facilityId={facilityId} />;
  }
  return <NsoEmployeeView />;
}

function NsoEmployeeView() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);      // { date, code, otWarn }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/nso/state");
      setState(data);
    } catch {
      toast.error("Failed to load New Schedule Open Shifts");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || !state) {
    return <div className="p-6 text-slate-500" data-testid="nso-loading">Loading…</div>;
  }
  if (!state.is_participant) {
    return (
      <div className="p-6" data-testid="nso-no-access">
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center">
          <div className="text-lg font-semibold text-slate-800">Not enrolled</div>
          <div className="text-sm text-slate-500 mt-1">
            The New Schedule Open Shifts board is only for Auburn / Solano union
            participants. Contact your manager if you should have access.
          </div>
        </div>
      </div>
    );
  }

  const targetMonthLabel = (() => {
    try {
      const [y, m] = state.target_month_id.slice(3).split("-").map(Number);
      return format(new Date(y, m - 1, 1), "MMMM yyyy");
    } catch { return state.target_month_id; }
  })();

  const days = monthGrid(state.target_month_id);
  const codeMap = new Map((state.shift_codes || []).map(c => [c.code, c]));
  const openByDate = new Map();
  (state.open_slots || []).forEach(s => {
    const arr = openByDate.get(s.date) || [];
    arr.push(s.shift_code);
    openByDate.set(s.date, arr);
  });
  const myReqByKey = new Map();
  (state.my_requests || []).forEach(r => {
    myReqByKey.set(`${r.date}::${r.shift_code}`, r);
  });
  const othersCountByKey = new Map();
  (state.all_requests || []).forEach(r => {
    if (r.employee_id === user?.id) return;
    const k = `${r.date}::${r.shift_code}`;
    othersCountByKey.set(k, (othersCountByKey.get(k) || 0) + 1);
  });
  const myAssignByDate = new Map();
  (state.my_assignments || []).forEach(a => { myAssignByDate.set(a.date, a); });

  const openReq = async (dateIso, code) => {
    // Pre-check OT via a HEAD-style dry run: attempt without wants_ot; the
    // backend returns 400 with the OT warning message when applicable.
    setConfirm({ date: dateIso, code, otWarn: null, phase: "checking" });
    try {
      const { data } = await api.post("/nso/request", {
        facility_id: state.facility_id, date: dateIso, shift_code: code,
        wants_ot: false,
      });
      // If it succeeded — done, no OT.
      toast.success(`Requested ${code} on ${dateIso}`);
      setConfirm(null);
      load();
      return data;
    } catch (e) {
      const msg = e?.response?.data?.detail || "";
      if (/overtime/i.test(msg)) {
        setConfirm({ date: dateIso, code, otWarn: msg, phase: "ot" });
      } else {
        toast.error(msg || "Request failed");
        setConfirm(null);
      }
    }
  };

  const submitOt = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      await api.post("/nso/request", {
        facility_id: state.facility_id, date: confirm.date,
        shift_code: confirm.code, wants_ot: true,
      });
      toast.success("Sent as OT-review request");
      setConfirm(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Request failed");
    } finally { setBusy(false); }
  };

  const cancel = async (reqId) => {
    if (!window.confirm("Cancel this request?")) return;
    try {
      await api.delete(`/nso/request/${reqId}`);
      toast.success("Cancelled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  // Deadline formatting
  const closesAtLabel = (() => {
    try {
      const dt = parseISO(state.closes_at);
      return mdyDowTime(dt);
    } catch { return state.closes_at; }
  })();

  return (
    <TooltipProvider delayDuration={100}>
    <div className="p-3 md:p-6 space-y-4" data-testid="nso-page">
      {/* Header banner */}
      <div className={`rounded-2xl border-2 p-4 flex flex-col md:flex-row md:items-center gap-3 ${state.is_open ? "border-emerald-400 bg-emerald-50" : "border-slate-300 bg-slate-50"}`}>
        <div className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 shadow ${state.is_open ? "bg-emerald-500 text-white" : "bg-slate-400 text-white"}`}>
          {state.is_open ? <Zap className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-black uppercase tracking-widest text-sm text-slate-800">
            {state.is_open ? `Requests open · closes ${closesAtLabel}` : "Requests are closed"}
          </div>
          <div className="text-xs text-slate-600 mt-0.5">
            Requesting <b>{targetMonthLabel}</b> open shifts. Highest-seniority
            non-OT request wins each slot when the window closes.
          </div>
        </div>
        {state.my_rank && (
          <div className="rounded-xl bg-white border border-slate-200 px-3 py-2 text-xs flex items-center gap-2" data-testid="nso-my-rank">
            <Trophy className="w-4 h-4 text-amber-500" />
            <span className="text-slate-500">Your seniority</span>
            <b className="text-slate-900">#{state.my_rank}</b>
            <span className="text-slate-400">of {state.participants?.length || 0}</span>
          </div>
        )}
      </div>

      {/* Nag banner if window open and no requests yet */}
      {state.is_open && (state.my_requests?.length || 0) === 0 && (
        <div className="rounded-2xl border-2 border-amber-400 bg-amber-50 p-3 flex items-center gap-3" data-testid="nso-nag">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
          <div className="text-sm text-amber-900">
            You haven't requested any open shifts yet — the window closes {closesAtLabel}.
          </div>
        </div>
      )}

      {/* Two columns: calendar of open shifts + my requests list */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-3">
          <div className="flex items-center justify-between mb-2 px-1">
            <div className="font-semibold text-slate-800 text-sm">{targetMonthLabel} — open shifts</div>
            <div className="text-[11px] text-slate-500">
              {(state.open_slots?.length || 0)} slots
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-widest text-slate-400 px-1 mb-1">
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => <div key={d}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((d) => {
              const iso = format(d, "yyyy-MM-dd");
              const inMonth = iso.startsWith(state.target_month_id.slice(3));
              const opens = openByDate.get(iso) || [];
              const mine = myAssignByDate.get(iso);
              return (
                <div key={iso}
                     className={`rounded-lg border p-1.5 min-h-[92px] text-[11px] ${inMonth ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50 opacity-50"}`}
                     data-testid={`nso-day-${iso}`}>
                  <div className="text-slate-400 font-semibold">{format(d, "d")}</div>
                  {mine && (
                    <div className="rounded bg-blue-50 border border-blue-200 text-blue-800 px-1 mt-1 truncate" title="Your shift">
                      {mine.shift_code}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {opens.map((code, i) => {
                      const key = `${iso}::${code}`;
                      const req = myReqByKey.get(key);
                      const others = othersCountByKey.get(key) || 0;
                      const codeMeta = codeMap.get(code);
                      const dept = codeMeta?.department || null;
                      const trained = !dept || (user?.departments_trained || []).includes(dept);
                      const canReq = state.is_open && !mine && !req && trained;
                      const tint = !trained ? "bg-slate-100 text-slate-400 border-slate-200"
                                : req?.wants_ot ? "bg-orange-100 text-orange-800 border-orange-400"
                                : req                ? "bg-emerald-100 text-emerald-800 border-emerald-400"
                                : "bg-amber-50 text-amber-800 border-amber-300";
                      return (
                        <Tooltip key={i}>
                          <TooltipTrigger asChild>
                            <button
                              disabled={!canReq}
                              onClick={() => canReq && openReq(iso, code)}
                              className={`inline-flex items-center gap-1 rounded border px-1 py-0.5 font-semibold ${tint} ${canReq ? "hover:brightness-95 cursor-pointer" : "cursor-default"}`}
                              data-testid={`nso-chip-${iso}-${code}`}
                            >
                              {code}
                              {req && <CheckCircle2 className="w-3 h-3" />}
                              {!trained && <Ban className="w-3 h-3" />}
                              {others > 0 && !req && <span className="text-[9px] opacity-70">({others})</span>}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="text-xs max-w-[280px]">
                            <div className="font-semibold text-white">{codeMeta?.label || code}</div>
                            <div className="text-[11px] text-slate-100 mt-0.5">
                              {codeMeta?.start && codeMeta?.end
                                ? `${codeMeta.start} – ${codeMeta.end}`
                                : "hours vary"}
                              {codeMeta?.hours ? ` · ${codeMeta.hours}h` : ""}
                            </div>
                            {dept && (
                              <div className="text-[11px] mt-0.5 flex items-center gap-1 text-slate-100">
                                <BadgeCheck className="w-3 h-3" />
                                {dept}
                              </div>
                            )}
                            {!trained && (
                              <div className="text-[11px] text-red-200 mt-1 font-semibold">
                                You aren't trained in {dept} — can't request.
                              </div>
                            )}
                            {trained && !mine && !req && state.is_open && (
                              <div className="text-[11px] text-emerald-200 mt-1">Tap to request</div>
                            )}
                            {mine && (
                              <div className="text-[11px] text-amber-200 mt-1">You're already scheduled that day</div>
                            )}
                            {others > 0 && !req && (
                              <div className="text-[11px] text-slate-200 mt-1">
                                {others} other{others > 1 ? "s" : ""} requested this
                              </div>
                            )}
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-3">
          <div className="font-semibold text-slate-800 text-sm mb-2">My requests</div>
          {(state.my_requests || []).length === 0 ? (
            <div className="text-xs text-slate-500 italic">Nothing yet.</div>
          ) : (
            <div className="space-y-2">
              {state.my_requests.map(r => (
                <div key={r.id} className={`rounded-lg border p-2 text-sm ${r.status === "assigned" ? "bg-emerald-50 border-emerald-300" : r.wants_ot ? "bg-orange-50 border-orange-300" : "bg-slate-50 border-slate-200"}`} data-testid={`nso-req-${r.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{r.date} · {r.shift_code}</div>
                    <span className={`text-[10px] uppercase tracking-widest font-bold ${r.status === "assigned" ? "text-emerald-700" : r.wants_ot ? "text-orange-700" : "text-slate-600"}`}>
                      {r.status === "pending" && r.wants_ot ? "OT-review" : r.status}
                    </span>
                  </div>
                  {r.wants_ot && (r.ot_warnings?.length || 0) > 0 && (
                    <div className="mt-1 text-[11px] text-orange-700">
                      OT: {r.ot_warnings.join(" · ")}
                    </div>
                  )}
                  {r.rejection_reason && (
                    <div className="mt-1 text-[11px] text-slate-600">
                      {r.rejection_reason}
                    </div>
                  )}
                  {(r.status === "pending" || r.status === "ot_review") && state.is_open && (
                    <button
                      onClick={() => cancel(r.id)}
                      className="mt-1 text-[11px] text-slate-500 hover:text-red-600 inline-flex items-center gap-1"
                      data-testid={`nso-cancel-${r.id}`}
                    >
                      <X className="w-3 h-3" /> cancel
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="mt-3 pt-3 border-t border-slate-100 text-[11px] text-slate-500 flex items-start gap-1.5">
            <Info className="w-3 h-3 mt-0.5 shrink-0" />
            Requests are ranked by <b className="text-slate-700">seniority</b> at close.
            OT-flagged requests stay pending for your manager to review.
          </div>
        </div>
      </div>

      {/* OT confirm dialog */}
      <Dialog open={!!confirm && confirm.phase === "ot"} onOpenChange={(v) => !v && setConfirm(null)}>
        <DialogContent className="sm:max-w-md" data-testid="nso-ot-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-700">
              <AlertTriangle className="w-5 h-5" />
              Would be overtime
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700 space-y-2">
            <p>
              Picking up <b>{confirm?.code}</b> on <b>{confirm?.date}</b> would exceed your OT rules:
            </p>
            <div className="rounded-md bg-orange-50 border border-orange-200 p-2 text-[12px] text-orange-800 whitespace-pre-line">
              {confirm?.otWarn}
            </div>
            <p className="text-xs text-slate-500">
              You can still request it — it'll be flagged for your manager to review after the window closes. You will not be auto-assigned to this shift.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)} data-testid="nso-ot-cancel">Never mind</Button>
            <Button className="bg-orange-600 hover:bg-orange-700" onClick={submitOt} disabled={busy} data-testid="nso-ot-confirm">
              {busy ? "Sending…" : "Send as OT-review request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </TooltipProvider>
  );
}
